import React, { useEffect } from "react";
import {
  HIDE_MAIN_PAYMENT_ERROR,
  SHOW_HOW_WOULD_YOU_LIKE_TO_GET_PAID
} from "@constants/actionTypes";
import { defaultAction } from "@actions/defaultAction";
import { Helmet } from "react-helmet";
import MailCheckSellWorkflow from "@components/MailCheckSellWorkflow";
import ACHSellWorkflow from "@components/ACHSellWorkflow";
import TradeOptionSellWorkflow from "@components/TradeOptionSellWorkflow";
import PayPalSellWorkflow from "@components/PayPalSellWorkflow";
import OrderVerificationModal from "@modules/OrderVerification/OrderVerificationModal";
import styled from "@emotion/styled";
import { loadScripts } from "@helpers/loadScripts";
import loadable from "@loadable/component";
const Loading = loadable(() =>
  import(/* webpackChunkName: "Loading" */ "../components/Loading")
);
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { initAppBrowser } from "@actions/initApp";
import { getSellBankAccountsWithAdded } from "@modules/Cart/selector";

const MainPayoutContainer = styled.div`
  padding-top: ${props => props.setPadding};

  @media (max-width: 950px) {
    padding-top: ${props => props.setPaddingMobile};
  }

  @media (max-width: 768px) {
    padding-top: ${props => props.setPaddingMobileShort};
  }

  @media (max-height: 750px) and (min-width: 768px) {
    padding-top: ${props => props.setPaddingMobile};
  }
`;

const MainPayoutContentContainer = styled.div`
  max-width: 904px;
  margin: 0 auto;
  margin-bottom: 100px;

  @media (max-width: 950px) {
    width: 100%;
  }

  @media (max-height: 950px) {
    position: relative;
    margin-bottom: 20px;
    padding-bottom: 155px;
  }
`;

const Payout = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const cardIds = useSelector(state => state.cartSell?.cardIds);
  const payoutMethod = useSelector(state => state.cartSell?.payoutMethod);
  const orderID = useSelector(
    state => state.customerInfo.orderID || state.orderVerification.orderUUID
  );
  const mainPaymentError = useSelector(
    state => state.customerInfo.mainPaymentError
  );
  const mainErrorMsg = useSelector(state => state.customerInfo.mainErrorMsg);
  const showChoosePhoneOrTextBox = useSelector(
    state => state.popupsActions.showChoosePhoneOrTextBox
  );
  const sellMsg = useSelector(state => state.popups.sellMsg);
  const customerInfoThunkRunning = useSelector(
    state => state.customerInfo._customerInfoThunkRunning
  );
  const signedIn = useSelector(state => state.initAppReducer.signedIn);
  const initialCartLoading = useSelector(
    state => state.popupsActions.initialCartLoading
  );
  const logoutThunkRunning = useSelector(
    state => state.customerInfo._logoutThunkRunning
  );
  const customerInfo = useSelector(state => state.customerInfo);
  const bannersList = useSelector(state => state.banners.bannersList);
  const defaultBankAccount = useSelector(
    state =>
      getSellBankAccountsWithAdded({
        paymentMethods: state.paymentMethods.sellPaymentMethods,
        defaultSellBankAccount: state.paymentMethods.defaultSellBankAccount
      }).allBankAccounts[0]
  );

  useEffect(() => {
    dispatch(initAppBrowser(["messages", "sell"]));
    loadScripts("https://www.paypalobjects.com/api/checkout.js", "paypalTag");
    dispatch(defaultAction(HIDE_MAIN_PAYMENT_ERROR));
  }, []);

  useEffect(() => {
    if (
      !customerInfoThunkRunning &&
      cardIds?.length < 1 &&
      !initialCartLoading
    ) {
      history.push("/sell-gift-cards/");
    } else if (
      !customerInfoThunkRunning &&
      signedIn &&
      !payoutMethod &&
      cardIds?.length > 0
    ) {
      dispatch(defaultAction(SHOW_HOW_WOULD_YOU_LIKE_TO_GET_PAID));
    }
  }, [customerInfoThunkRunning, initialCartLoading]);

  useEffect(() => {
    if (logoutThunkRunning) {
      history.push("/");
    }
  }, [logoutThunkRunning]);

  useEffect(() => {
    if (!signedIn && cardIds?.length < 1 && !initialCartLoading) {
      history.push("/sell-gift-cards/");
    }
  }, [signedIn]);

  const initialValues = {
    acceptTerms: false,
    primaryResidence: {
      fullname: `${customerInfo.firstname} ${customerInfo.lastname}`,
      firstname: customerInfo.firstname,
      lastname: customerInfo.lastname,
      street: customerInfo.street,
      street2: customerInfo.street2,
      city: customerInfo.city,
      state: customerInfo.state,
      zip: customerInfo.zip
    },
    verification: {
      sameAsPrimary: true
    }
  };
  return (
    <MainPayoutContainer
      setPadding={bannersList.length ? "67px" : "170px"}
      setPaddingMobile={bannersList.length ? "38px" : "145px"}
      setPaddingMobileShort={bannersList.length ? "38px" : "125px"}
    >
      <Helmet
        title={"Sell Gift Cards for Cash Online - Up to 92% | CardCash"}
      />

      {initialCartLoading ? (
        <Loading />
      ) : (
        <MainPayoutContentContainer>
          {showChoosePhoneOrTextBox && (
            <OrderVerificationModal
              orderUUID={orderID}
              type="sell"
              page={false}
              activeSellOrder={true}
            />
          )}
          <div>
            {!!sellMsg && (
              <div
                className="alert alert-info"
                role="alert"
                dangerouslySetInnerHTML={{ __html: sellMsg }}
              />
            )}

            {!!mainPaymentError && (
              <div
                className="alert alert-danger animated fadeInDown"
                role="alert"
                style={{ marginLeft: "12px", marginRight: "13px" }}
              >
                <strong>Oops! </strong> {mainErrorMsg}
              </div>
            )}

            {payoutMethod === "MAILCHECK" && (
              <MailCheckSellWorkflow
                initialValues={{
                  ...initialValues,
                  shippingAddress: {
                    sameAsResidence: true
                  },
                  verification: {
                    sameAsShipping: true
                  }
                }}
              />
            )}

            {payoutMethod === "ACH" && (
              <ACHSellWorkflow
                initialValues={{
                  ...initialValues,
                  enterACH: {
                    achRow: defaultBankAccount
                  }
                }}
              />
            )}

            {payoutMethod === "TRADEOPTION" && (
              <TradeOptionSellWorkflow
                initialValues={{
                  ...initialValues
                }}
              />
            )}

            {payoutMethod === "PAYPALEXPRESS" && (
              <PayPalSellWorkflow
                initialValues={{
                  ...initialValues
                }}
              />
            )}
          </div>
        </MainPayoutContentContainer>
      )}
    </MainPayoutContainer>
  );
};

export default Payout;
